import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { NOTIFICATION_TYPES } from "@/constants/notification-types";
import { useMapPosts } from "@/hooks/useMapPosts.web";
import { useThemeColor } from "@/hooks/useThemeColor";
import { RecencyPreset } from "@/types/mapPostsFilters";
import {
  aggregateNearbyPosts,
  EventTypeKey,
} from "@/utils/aggregateNearbyPosts";
import { LatLng, requestBrowserPosition } from "@/utils/geo.web";
import Ionicons from "@expo/vector-icons/Ionicons";
import { router } from "expo-router";
import Head from "expo-router/head";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

const REPORT_RADIUS_METERS = 500;
const REPORT_RECENCY: RecencyPreset = "30d";
const POST_LIST_MAX_HEIGHT = 240;

const RENDER_AI_SUMMARY = false;

type GeoStatus =
  | "idle"
  | "requesting"
  | "granted"
  | "denied"
  | "unavailable"
  | "timeout";

const goToPost = (postId: string) => {
  router.push({
    pathname: "/(tabs)/post/[postId]",
    params: { postId },
  });
};

export default function AreaReportScreen() {
  const { t } = useTranslation();
  const tintColor = useThemeColor({}, "tint");

  const [userPos, setUserPos] = useState<LatLng | null>(null);
  const [geoStatus, setGeoStatus] = useState<GeoStatus>("idle");
  const [expandedKey, setExpandedKey] = useState<EventTypeKey | null>(null);

  const captureLocation = useCallback(async () => {
    setGeoStatus("requesting");
    const result = await requestBrowserPosition();
    if (result.kind === "ok") {
      setUserPos(result.position);
      setGeoStatus("granted");
    } else {
      setUserPos(null);
      setGeoStatus(result.kind);
    }
  }, []);

  useEffect(() => {
    void captureLocation();
  }, [captureLocation]);

  const { posts, loading } = useMapPosts({ recency: REPORT_RECENCY });

  const aggregates = useMemo(
    () =>
      userPos
        ? aggregateNearbyPosts(posts, userPos, {
            radiusMeters: REPORT_RADIUS_METERS,
          })
        : null,
    [posts, userPos],
  );

  const toggleExpanded = useCallback((key: EventTypeKey) => {
    setExpandedKey((current) => (current === key ? null : key));
  }, []);

  const renderBody = () => {
    if (geoStatus === "requesting" || geoStatus === "idle") {
      return (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={tintColor} />
          <ThemedText style={styles.statusText}>
            {t("AREA_REPORT_LOADING")}
          </ThemedText>
        </View>
      );
    }

    if (geoStatus === "denied") {
      return (
        <View style={styles.center}>
          <ThemedText style={styles.emptyTitle}>
            {t("AREA_REPORT_LOCATION_DENIED")}
          </ThemedText>
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: tintColor }]}
            onPress={captureLocation}
          >
            <ThemedText style={styles.retryButtonText}>
              {t("AREA_REPORT_LOCATION_RETRY")}
            </ThemedText>
          </TouchableOpacity>
        </View>
      );
    }

    if (geoStatus === "unavailable" || geoStatus === "timeout" || !userPos) {
      return (
        <View style={styles.center}>
          <ThemedText style={styles.emptyTitle}>
            {t("AREA_REPORT_LOCATION_UNAVAILABLE")}
          </ThemedText>
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: tintColor }]}
            onPress={captureLocation}
          >
            <ThemedText style={styles.retryButtonText}>
              {t("AREA_REPORT_LOCATION_RETRY")}
            </ThemedText>
          </TouchableOpacity>
        </View>
      );
    }

    if (loading || !aggregates) {
      return (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={tintColor} />
          <ThemedText style={styles.statusText}>
            {t("AREA_REPORT_LOADING")}
          </ThemedText>
        </View>
      );
    }

    if (aggregates.cards.length === 0) {
      return (
        <View style={styles.center}>
          <ThemedText style={styles.emptyTitle}>
            {t("AREA_REPORT_EMPTY")}
          </ThemedText>
        </View>
      );
    }

    return (
      <View style={styles.cardList}>
        {aggregates.cards.map((card) => {
          const meta = NOTIFICATION_TYPES[card.key];
          const isExpanded = expandedKey === card.key;
          return (
            <View
              key={card.key}
              style={[styles.card, { borderColor: meta.severity.border }]}
            >
              <TouchableOpacity
                style={[
                  styles.cardHeader,
                  { backgroundColor: meta.severity.background },
                ]}
                onPress={() => toggleExpanded(card.key)}
                accessibilityRole="button"
                accessibilityLabel={t(meta.title)}
              >
                <ThemedText style={styles.cardIcon}>{meta.icon}</ThemedText>
                <View style={styles.cardBody}>
                  <ThemedText
                    style={[styles.cardTitle, { color: meta.severity.text }]}
                  >
                    {t(meta.title)}
                  </ThemedText>
                  <ThemedText
                    style={[styles.cardMeta, { color: meta.severity.text }]}
                  >
                    {t("AREA_REPORT_NEAREST_X_METERS", {
                      meters: Math.round(card.nearestMeters),
                    })}
                  </ThemedText>
                </View>
                <ThemedText
                  style={[styles.cardCount, { color: meta.severity.text }]}
                >
                  {card.count}
                </ThemedText>
                <Ionicons
                  name={isExpanded ? "chevron-up" : "chevron-down"}
                  size={18}
                  color={meta.severity.text}
                />
              </TouchableOpacity>

              {isExpanded && (
                <View style={styles.postList}>
                  <ScrollView
                    style={{ maxHeight: POST_LIST_MAX_HEIGHT }}
                    nestedScrollEnabled
                  >
                    {card.posts.map((p) => (
                      <TouchableOpacity
                        key={p.id}
                        style={styles.postItem}
                        onPress={() => goToPost(p.id)}
                        accessibilityRole="link"
                      >
                        <ThemedText style={styles.postIcon}>
                          {meta.icon}
                        </ThemedText>
                        <ThemedText
                          style={[
                            styles.postDescription,
                            { color: tintColor },
                          ]}
                          numberOfLines={1}
                          ellipsizeMode="tail"
                        >
                          {p.description.trim() ||
                            t("AREA_REPORT_NO_DESCRIPTION")}
                        </ThemedText>
                        <ThemedText style={styles.postDistance}>
                          {Math.round(p.distanceMeters)} m
                        </ThemedText>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}
            </View>
          );
        })}

        {RENDER_AI_SUMMARY && (
          // TODO(ai-summary): call summarization endpoint with `aggregates`
          <View />
        )}
      </View>
    );
  };

  return (
    <ThemedView style={styles.container}>
      <Head>
        <title>{t("PAGE_TITLE_REPORT_AREA")}</title>
      </Head>
      <CustomHeader
        title={t("AREA_REPORT_TITLE")}
        type="subtitle"
        variant="tab"
        showBackButton
      />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <ThemedText style={styles.scopeLabel}>
          {t("AREA_REPORT_SCOPE_500M_30D")}
        </ThemedText>
        {renderBody()}
        <ThemedText style={styles.caveat}>
          {t("AREA_REPORT_COVERAGE_CAVEAT")}
        </ThemedText>
      </ScrollView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: {
    padding: 20,
    paddingBottom: 60,
    gap: 16,
    maxWidth: 680,
    width: "100%",
    alignSelf: "center",
  },
  scopeLabel: {
    fontSize: 14,
    opacity: 0.7,
    textAlign: "center",
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 40,
    gap: 16,
  },
  statusText: { fontSize: 14, opacity: 0.7 },
  emptyTitle: { fontSize: 16, textAlign: "center", paddingHorizontal: 12 },
  retryButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 999,
  },
  retryButtonText: { color: "white", fontWeight: "700" },
  cardList: { gap: 10 },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  cardIcon: { fontSize: 22 },
  cardBody: { flex: 1, gap: 2 },
  cardTitle: { fontSize: 15, fontWeight: "600" },
  cardMeta: { fontSize: 12, opacity: 0.8 },
  cardCount: { fontSize: 22, fontWeight: "700" },
  postList: {},
  postItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  postIcon: { fontSize: 18 },
  postDescription: {
    flex: 1,
    fontSize: 13,
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  postDistance: { fontSize: 12, opacity: 0.6 },
  caveat: {
    fontSize: 11,
    opacity: 0.6,
    textAlign: "center",
    marginTop: 8,
    paddingHorizontal: 12,
  },
});
